import { useState, useEffect } from 'react';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';
import { logger } from '@/lib/utils/logger';

export function useUserPurchaseStatus(experienceId: string) {
  const { user } = useUnifiedAuth();
  const [hasPurchased, setHasPurchased] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseDetails, setPurchaseDetails] = useState<any>(null);

  useEffect(() => {
    let isMounted = true;

    const checkPurchaseStatus = async () => {
      if (!user || !experienceId) {
        setHasPurchased(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Check for confirmed/paid bookings for this experience and user
        const { data: bookings, error } = await supabase
          .from('bookings')
          .select('*')
          .eq('user_id', user.id)
          .eq('experience_id', experienceId)
          .in('status', ['confirmed'])
          .in('payment_status', ['paid'])
          .order('created_at', { ascending: false });

        if (error) {
          logger.error('Error checking purchase status', { error });
          return;
        }

        if (isMounted) {
          const purchased = bookings && bookings.length > 0;
          setHasPurchased(purchased);
          setPurchaseDetails(purchased ? bookings[0] : null);
        }
      } catch (error) {
        logger.error('Error in checkPurchaseStatus', { error });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkPurchaseStatus();

    return () => {
      isMounted = false;
    };
  }, [user, experienceId]);

  return {
    hasPurchased,
    isLoading,
    purchaseDetails,
    refresh: () => {
      // Re-run the check
      if (user && experienceId) {
        setIsLoading(true);
        // The useEffect will handle the actual check
      }
    }
  };
} 